import { CountUp } from 'countup.js';

const COUNTUP_DURATION_SECONDS = 1.4;
const AUTO_CLOSE_MS = 5000;
const MOBILE_BREAKPOINT = 781;

const parseStatTarget = value => {
  const normalized = String(value || '').replace(/,/g, '').trim();
  if (!/^-?\d*\.?\d+$/.test(normalized)) {
    return null;
  }

  const target = Number.parseFloat(normalized);
  if (!Number.isFinite(target)) {
    return null;
  }

  const decimalPart = normalized.split('.')[1] || '';
  return {
    value: target,
    decimals: decimalPart.length,
  };
};

const animateCardNumber = card => {
  const numberEl = card.querySelector('[data-stat-number]');
  if (!numberEl) {
    return;
  }

  if (numberEl.dataset.counted === '1') {
    return;
  }

  if (!numberEl.dataset.statTarget) {
    numberEl.dataset.statTarget = numberEl.textContent.trim();
  }

  const parsed = parseStatTarget(numberEl.dataset.statTarget);
  if (!parsed) {
    return;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    numberEl.textContent = numberEl.dataset.statTarget;
    numberEl.dataset.counted = '1';
    card.classList.remove('is-counting');
    card.classList.add('is-counted');
    return;
  }

  const counter = new CountUp(numberEl, parsed.value, {
    startVal: 0,
    duration: COUNTUP_DURATION_SECONDS,
    decimalPlaces: parsed.decimals,
    separator: ',',
    useGrouping: true,
  });

  if (counter.error) {
    numberEl.textContent = numberEl.dataset.statTarget;
    numberEl.dataset.counted = '1';
    card.classList.remove('is-counting');
    card.classList.add('is-counted');
    return;
  }

  card.classList.add('is-counting');

  counter.start(() => {
    numberEl.dataset.counted = '1';
    card.classList.remove('is-counting');
    card.classList.add('is-counted');
  });
};

const setActiveCard = (cards, activeIndex) => {
  cards.forEach((card, index) => {
    card.classList.toggle('is-active', index === activeIndex);
  });
};

const setAllCardsActive = cards => {
  cards.forEach(card => card.classList.add('is-active'));
};

const initStatCards = root => {
  const cards = Array.from(root.querySelectorAll('[data-stat-card]'));
  if (cards.length === 0) {
    return;
  }

  const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

  const defaultActive = Math.max(
    0,
    Math.min(cards.length - 1, Number.parseInt(root.dataset.defaultActive || '1', 10) - 1),
  );

  let activeIndex = defaultActive;
  let hasEnteredView = false;
  let autoCloseTimer = null;

  const animateAllCardNumbers = () => {
    cards.forEach(card => animateCardNumber(card));
  };

  const clearAutoCloseTimer = () => {
    if (autoCloseTimer !== null) {
      window.clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
  };

  const scheduleAutoClose = () => {
    if (isMobile()) {
      return;
    }

    clearAutoCloseTimer();
    autoCloseTimer = window.setTimeout(() => {
      activeIndex = -1;
      setActiveCard(cards, activeIndex);
    }, AUTO_CLOSE_MS);
  };

  const updateActiveCard = nextIndex => {
    if (isMobile()) {
      return;
    }

    activeIndex = nextIndex;
    setActiveCard(cards, activeIndex);
    scheduleAutoClose();
  };

  if (isMobile()) {
    setAllCardsActive(cards);
    root.classList.add('is-mobile-static');
  } else {
    setActiveCard(cards, defaultActive);
    scheduleAutoClose();
  }

  cards.forEach((card, index) => {
    card.addEventListener('mouseenter', () => {
      updateActiveCard(index);
    });

    card.addEventListener('focusin', () => {
      updateActiveCard(index);
    });
  });

  if (!isMobile()) {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.target !== root) {
            return;
          }

          if (entry.isIntersecting && !hasEnteredView) {
            hasEnteredView = true;
            animateAllCardNumbers();
          }
        });
      },
      {
        threshold: 0.35,
      },
    );

    observer.observe(root);
  }
};

const StatCards = () => {
  const groups = Array.from(document.querySelectorAll('[data-stat-cards]'));
  groups.forEach(initStatCards);
};

export default StatCards;
